import React, { useState, useEffect, useCallback } from "react";
import ColorPickerCanvas from "./ColorPickerCanvas";
import HueSlider from "./HueSlider";
import QuickColors from "./QuickColors";
import { hexToHsv, hsvToHex, isValidHex } from "./colorUtils";
import "./ColorPicker.sass";

/**
 * Full color picker: gradient canvas, hue slider, quick-pick swatches.
 *
 * @param {object}   props
 * @param {string}   props.value    - current color as hex string, e.g. "#2FA766"
 * @param {Function} props.onChange - (hex: string) => void
 */
const ColorPicker = ({ value, onChange }) => {
  const parseInitial = useCallback((hex) => {
    if (!isValidHex(hex)) return { h: 0, s: 1, v: 1 };

    return hexToHsv(hex);
  }, []);

  const [hsv, setHsv] = useState(() => parseInitial(value));

  // Sync external value changes (e.g. "Use default colors")
  useEffect(() => {
    if (isValidHex(value)) {
      setHsv(hexToHsv(value));
    }
  }, [value]);

  const handleCanvasChange = useCallback(
    (s, v) => {
      const next = { ...hsv, s, v };

      setHsv(next);
      onChange(hsvToHex(next.h, next.s, next.v));
    },
    [hsv, onChange],
  );

  const handleHueChange = useCallback(
    (h) => {
      const next = { ...hsv, h };

      setHsv(next);
      onChange(hsvToHex(next.h, next.s, next.v));
    },
    [hsv, onChange],
  );

  const handleQuickSelect = useCallback(
    (hex) => {
      if (!isValidHex(hex)) return;

      const next = hexToHsv(hex);

      setHsv(next);
      onChange(hex);
    },
    [onChange],
  );

  const currentHex = hsvToHex(hsv.h, hsv.s, hsv.v);

  return (
    <div className="color-picker">
      <div className="color-picker__current-row">
        <span className="color-picker__current-label">Current color:</span>
        <span
          className="color-picker__current-swatch"
          style={{ backgroundColor: currentHex }}
          aria-label={currentHex}
        />
      </div>
      <ColorPickerCanvas
        hue={hsv.h}
        saturation={hsv.s}
        brightness={hsv.v}
        onChange={handleCanvasChange}
      />
      <HueSlider hue={hsv.h} onChange={handleHueChange} />
      <QuickColors currentColor={currentHex} onSelect={handleQuickSelect} />
    </div>
  );
};

export default ColorPicker;
